import { Component, OnInit, ChangeDetectorRef, ViewChildren, QueryList } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { QuillEditorComponent, QuillModule } from 'ngx-quill';
import { PrimengModule } from '@app/shared/modules/primeng.module';
import { SystemConfigurationService } from '../system-configuration.service';
import { AlertService } from '@app/@core/services/alert.service';
import { ContentManagementModalComponent } from './modal/content-management-modal.component';
import Quill from 'quill';

const FontStyle = Quill.import('attributors/style/font') as any;
const SizeStyle = Quill.import('attributors/style/size') as any;

FontStyle.whitelist = [
  'Arial', 'Calibri', 'Cambria', 'Comic Sans MS', 'Courier New',
  'Garamond', 'Georgia', 'Impact', 'Lucida Console', 'Montserrat',
  'Palatino Linotype', 'Tahoma', 'Times New Roman', 'Trebuchet MS', 'Verdana'
];

SizeStyle.whitelist = [
  '8px', '9px', '10px', '11px', '12px', '14px', '16px', '18px',
  '20px', '22px', '24px', '26px', '28px', '36px', '48px', '72px'
];

Quill.register(FontStyle, true);
Quill.register(SizeStyle, true);

// Register additional list styles (alphabetical + Roman numerals) alongside
// Quill's built-in "ordered" and "bullet" list formats. Quill 2 stores the
// active list variant in the `data-list` attribute of each <li>, so any string
// value passed via quill.format('list', value) is round-tripped through HTML
// automatically. We only need to register toolbar icons and provide CSS for
// the markers.
const QuillIcons = Quill.import('ui/icons') as Record<string, any>;
const listIcons = QuillIcons['list'] as Record<string, string>;

listIcons['lower-alpha'] =
  '<svg viewBox="0 0 18 18">' +
  '<line class="ql-stroke" x1="7" x2="15" y1="4" y2="4"></line>' +
  '<line class="ql-stroke" x1="7" x2="15" y1="9" y2="9"></line>' +
  '<line class="ql-stroke" x1="7" x2="15" y1="14" y2="14"></line>' +
  '<text class="ql-fill" x="1" y="6" style="font-size:6px;font-family:serif;">a</text>' +
  '<text class="ql-fill" x="1" y="11" style="font-size:6px;font-family:serif;">b</text>' +
  '<text class="ql-fill" x="1" y="16" style="font-size:6px;font-family:serif;">c</text>' +
  '</svg>';

listIcons['upper-alpha'] =
  '<svg viewBox="0 0 18 18">' +
  '<line class="ql-stroke" x1="7" x2="15" y1="4" y2="4"></line>' +
  '<line class="ql-stroke" x1="7" x2="15" y1="9" y2="9"></line>' +
  '<line class="ql-stroke" x1="7" x2="15" y1="14" y2="14"></line>' +
  '<text class="ql-fill" x="1" y="6" style="font-size:6px;font-family:serif;">A</text>' +
  '<text class="ql-fill" x="1" y="11" style="font-size:6px;font-family:serif;">B</text>' +
  '<text class="ql-fill" x="1" y="16" style="font-size:6px;font-family:serif;">C</text>' +
  '</svg>';

listIcons['lower-roman'] =
  '<svg viewBox="0 0 18 18">' +
  '<line class="ql-stroke" x1="7" x2="15" y1="4" y2="4"></line>' +
  '<line class="ql-stroke" x1="7" x2="15" y1="9" y2="9"></line>' +
  '<line class="ql-stroke" x1="7" x2="15" y1="14" y2="14"></line>' +
  '<text class="ql-fill" x="0" y="6" style="font-size:6px;font-family:serif;">i</text>' +
  '<text class="ql-fill" x="0" y="11" style="font-size:6px;font-family:serif;">ii</text>' +
  '<text class="ql-fill" x="0" y="16" style="font-size:6px;font-family:serif;">iii</text>' +
  '</svg>';

listIcons['upper-roman'] =
  '<svg viewBox="0 0 18 18">' +
  '<line class="ql-stroke" x1="7" x2="15" y1="4" y2="4"></line>' +
  '<line class="ql-stroke" x1="7" x2="15" y1="9" y2="9"></line>' +
  '<line class="ql-stroke" x1="7" x2="15" y1="14" y2="14"></line>' +
  '<text class="ql-fill" x="0" y="6" style="font-size:6px;font-family:serif;">I</text>' +
  '<text class="ql-fill" x="0" y="11" style="font-size:6px;font-family:serif;">II</text>' +
  '<text class="ql-fill" x="0" y="16" style="font-size:6px;font-family:serif;">III</text>' +
  '</svg>';

export interface ContentSection {
  key: string;
  label: string;
  content: string;
  savedContent: string;
  isDirty: boolean;
}

@Component({
  selector: 'app-content-management',
  standalone: true,
  imports: [CommonModule, FormsModule, QuillModule, PrimengModule, ContentManagementModalComponent],
  templateUrl: './content-management.component.html',
  styleUrls: ['./content-management.component.scss']
})
export class ContentManagementComponent implements OnInit {
  sections: ContentSection[] = [
    { key: '1', label: 'Local Education Agency', content: '', savedContent: '', isDirty: false },
    { key: '2', label: 'Fingerprinting Vendor', content: '', savedContent: '', isDirty: false },
    { key: '3', label: 'Education Program Provider', content: '', savedContent: '', isDirty: false },
    { key: '4', label: 'Applicant/Licensee', content: '', savedContent: '', isDirty: false }
  ];

  private readonly valueMap: Record<string, string> = {
    '1': 'LEA',
    '2': 'FPV',
    '3': 'EPP',
    '4': 'EDUCATOR'
  };

  quillModules = {
    toolbar: [
      [{ font: FontStyle.whitelist }],
      [{ size: SizeStyle.whitelist }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ color: [] }, { background: [] }],
      [{ script: 'sub' }, { script: 'super' }],
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [
        { list: 'ordered' },
        { list: 'bullet' },
        { list: 'lower-alpha' },
        { list: 'upper-alpha' },
        { list: 'lower-roman' },
        { list: 'upper-roman' }
      ],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ direction: 'rtl' }],
      [{ align: [] }],
      ['blockquote', 'code-block'],
      ['link', 'image', 'video'],
      ['clean']
    ],
    history: {
      delay: 1000,
      maxStack: 100,
      userOnly: true
    }
  };

  @ViewChildren(QuillEditorComponent) editors!: QueryList<QuillEditorComponent>;

  constructor(
    private readonly systemConfigService: SystemConfigurationService,
    private readonly alertService: AlertService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadContent();
  }

  onEditorCreated(editor: any): void {
    const toolbar = editor.getModule('toolbar');

    const makeFormatHandler = (formatName: string) => {
      return (value: any) => {
        const range = editor.getSelection();
        if (range && range.length > 0) {
          editor.formatText(range.index, range.length, formatName, value, 'user');
          setTimeout(() => editor.setSelection(range.index, range.length));
        } else {
          editor.format(formatName, value, 'user');
        }
      };
    };

    toolbar.addHandler('font', makeFormatHandler('font'));
    toolbar.addHandler('size', makeFormatHandler('size'));

    const syncToolbar = () => {
      const range = editor.getSelection();
      if (!range) return;
      const formats = editor.getFormat(range);
      this.updatePickerDisplay(toolbar.container, 'font', formats['font']);
      this.updatePickerDisplay(toolbar.container, 'size', formats['size']);
    };

    editor.on('selection-change', (range: any) => {
      if (range) syncToolbar();
    });
    editor.on('text-change', () => setTimeout(syncToolbar));
  }

  private updatePickerDisplay(toolbarEl: HTMLElement, format: string, value: any): void {
    if (!toolbarEl) return;
    const picker = toolbarEl.querySelector(`.ql-picker.ql-${format}`) as HTMLElement;
    if (!picker) return;

    const label = picker.querySelector('.ql-picker-label') as HTMLElement;
    const items = picker.querySelectorAll('.ql-picker-item');

    picker.querySelector('.ql-selected')?.classList.remove('ql-selected');

    if (value && typeof value === 'string') {
      items.forEach((item: Element) => {
        if ((item as HTMLElement).dataset['value'] === value) {
          item.classList.add('ql-selected');
        }
      });
      label?.setAttribute('data-value', value);
      label?.classList.add('ql-active');
    } else {
      label?.removeAttribute('data-value');
      label?.classList.remove('ql-active');
    }
  }

  onAccordionOpen(event: any): void {
    const index = event.index;
    const section = this.sections[index];
    if (!section) return;
    this.systemConfigService.getContentByKey(section.key).subscribe({
      next: (response: any) => {
        const record = response?.data?.data;
        if (record) {
          const optionaldata = typeof record.optionaldata === 'string'
            ? JSON.parse(record.optionaldata)
            : record.optionaldata;
          const content = optionaldata?.content ?? '';
          section.content = content;
          section.savedContent = content;
          section.isDirty = false;
          this.cdr.detectChanges();
          setTimeout(() => {
            const editorComp = this.editors?.toArray()?.[index];
            const quill = editorComp?.quillEditor;
            if (quill) {
              quill.root.innerHTML = content;
              quill.update('silent');
            }
          });
        }
      },
      error: () => {
        this.alertService.error('Failed to load content.');
      }
    });
  }

  private loadContent(): void {
    this.systemConfigService.getContentManagementData().subscribe({
      next: (response: any) => {
        const items = response?.data?.data;
        if (items) {
          for (const item of items) {
            const section = this.sections.find(s => s.key === item.key);
            if (section) {
              const optionaldata = typeof item.optionaldata === 'string'
                ? JSON.parse(item.optionaldata)
                : item.optionaldata;
              const content = optionaldata?.content ?? '';
              section.content = content;
              section.savedContent = content;
            }
          }
        }
      },
      error: () => {
        this.alertService.error('Failed to load content.');
      }
    });
  }

  private normalizeContent(content: string): string {
    if (!content) return '';
    return content.replace(/<p><br><\/p>/g, '').trim();
  }

  onContentChanged(section: ContentSection, event?: any): void {
    const currentHtml = event?.editor?.root?.innerHTML ?? event?.html ?? section.content ?? '';
    const normalizedCurrent = this.normalizeContent(currentHtml);
    const normalizedSaved = this.normalizeContent(section.savedContent);
    section.isDirty = normalizedCurrent !== normalizedSaved;
  }

  onSave(section: ContentSection): void {
    const index = this.sections.indexOf(section);
    const editorComp = this.editors?.toArray()?.[index];
    const content = editorComp?.quillEditor?.root?.innerHTML ?? section.content;
    section.content = content;
    const payload = {
      key: section.key,
      value: this.valueMap[section.key],
      content
    };
    this.systemConfigService.saveContentManagementData(payload).subscribe({
      next: () => {
        section.savedContent = section.content;
        section.isDirty = false;
        this.alertService.success('Content saved successfully.');
      },
      error: () => {
        this.alertService.error('Failed to save content.');
      }
    });
  }

  onUndo(section: ContentSection): void {
    const index = this.sections.indexOf(section);
    const editorComp = this.editors?.toArray()?.[index];
    const quill = editorComp?.quillEditor;
    if (quill) {
      quill.history.undo();
    }
  }

  onRedo(section: ContentSection): void {
    const index = this.sections.indexOf(section);
    const editorComp = this.editors?.toArray()?.[index];
    const quill = editorComp?.quillEditor;
    if (quill) {
      quill.history.redo();
    }
  }
}
